// ===================================================================================================
// Filename: src/app/app.component.ts
// VERSION: v2.0.0 - Complete Access Control System Integration
// DATE: 2026-01-09
//
// CHANGES v2.0.0:
//   ✅ ADDED: CAL (Client Access License) Status Check on app load
//   ✅ ADDED: Periodic CAL refresh (every 10 minutes)
//   ✅ ADDED: Observable subscription for CAL status changes
//   ✅ IMPLEMENTED: OnInit and OnDestroy lifecycle hooks
//
// FEATURES:
//   - Automatic CAL check on app initialization
//   - Periodic refresh for continuous license monitoring
//   - Optional warning notifications for expiring licenses
//   - Graceful cleanup on app destroy
// ===================================================================================================
// ===================================================================================================
// Filename: src/app/app.component.ts
// VERSION: v3.0.0 - Complete Access Control System Integration (FIXED PATHS)
// DATE: 2026-01-10
//
// CHANGES v3.0.0:
//   ✅ FIXED: Import path for CalStatusService (services/ statt core/services/)
//   ✅ VERIFIED: All paths match actual filesystem structure
// ===================================================================================================
// ═══════════════════════════════════════════════════════════════════════════════
// FILE: src/app/app.component.ts
// VERSION: v4.0.0 - Simplified with Route Guard System
// DATE: 2025-01-24
//
// CHANGES v4.0.0:
//   ✅ REMOVED: CAL check from AppComponent (moved to Route Guard)
//   ✅ SIMPLIFIED: No more lifecycle complexity
//   ✅ IMPROVED: Separation of concerns - Guards handle access control
//
// ARCHITECTURE:
//   - CAL checks now handled by: src/app/guards/cal-status.guard.ts
//   - /login is ALWAYS accessible
//   - All other routes protected by canActivate: [calStatusGuard]
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// ERGÄNZUNG v5.0.0 — 2026-08-09 · Live-Nachrichten app-weit
//
//   Die Hülle baut die WebSocket-Verbindung auf und hält das Popup bereit.
//   Vorher geschah beides in der Posteingang-Seite. Das hiess: wer im
//   Task-Editor arbeitete, hatte keine Verbindung und bekam nichts mit.
//
//   Nach jeder Navigation abseits der Anmeldeseite wird die Verbindung
//   sichergestellt — der Handshake auf /login wird ohne Sitzung zu Recht
//   abgelehnt, und die Anmeldung läuft ohne Neuladen weiter.
//
//   ceeq.it: Item #78 in _34 · Tasks #309, #310
// ═══════════════════════════════════════════════════════════════════════════════

import { Component, OnInit, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MessagePopupComponent } from './components/message-popup/message-popup.component';
import { MessagePopupService } from './services/message-popup.service';
import { AskPanelComponent } from './components/ask-panel/ask-panel.component';
import { RequestReminderComponent } from './components/request-reminder/request-reminder.component';
import { EisenhowerPanelComponent } from './components/eisenhower-panel/eisenhower-panel.component';
import { AgentStatusComponent } from './components/agent-status/agent-status.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, MessagePopupComponent, AskPanelComponent, RequestReminderComponent, EisenhowerPanelComponent, AgentStatusComponent],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {

  title = 'Just CEEQ.IT!';

  private popup = inject(MessagePopupService);
  private router = inject(Router);

  // MOD 2026-10-23 (PIA): Eisenhower-Fenster Alt+N hängt an derselben Bedingung (mitAnfragen).
  // MOD 2026-10-12 (PIA, _40 #223): Ask-Panel und Erinnerung nur mit Sitzung —
  // auf /login und /app-management/login fehlt sie, jeder Abruf wäre ein 401.
  readonly mitAnfragen = signal(false);

  constructor() {
    console.log('[AppComponent] 🚀 CEEQ.IT Application Starting...');
  }

  ngOnInit(): void {
    console.log('[AppComponent] 🔍 Initializing application...');

    // ℹ️ CAL (Client Access License) check moved to Route Guard
    // ℹ️ See: src/app/guards/cal-status.guard.ts
    // ℹ️ All protected routes use: canActivate: [calStatusGuard]
    // ℹ️ Only /login is accessible without CAL

    // Live-Nachrichten: eine Verbindung für die ganze App.
    this.popup.starten();

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        if (!e.urlAfterRedirects.startsWith('/login')) {
          this.popup.sicherstellenVerbunden();
        }
        const u = e.urlAfterRedirects;
        this.mitAnfragen.set(!(u.startsWith('/login') || u.startsWith('/app-management/login')
          || u.startsWith('/no-license') || u.startsWith('/self-assign') || u === '/'));
      });

    console.log('[AppComponent] ✅ Application initialized successfully');
  }
}

/* END OF FILE */
